import { createStore, applyMiddleware, compose, StoreEnhancer } from "redux";
import thunk, { ThunkMiddleware, ThunkDispatch } from "redux-thunk";
import rootReducer from "../reducers";
import { Authentication } from "../../model/Authentication";
import { AppState } from "./AppState";
import * as ActionTypes from "../actions/actionTypes";
import { PendingAuthentication } from "../../model/PendingAuthentication";
import { saveState, loadState } from "./localStorage";
import { lazyThrottle } from "../../util/throttle";
import _debug from "debug";
import { LogoutCompleted } from "../../model/LogoutCompleted";
import { AppError } from "../../model/AppError";
const debug = _debug("store");

/**
 * Empty / initial Redux store state.
 */
export const EMPTY_STATE: AppState = {
  authentication: null,
  errors: [],
  inProgress: [],
  logoutCompleted: null,
  pendingAuthentication: null,
  licenses: undefined,
};


// Initialize the store
const persistedState =
  (global as any).stateForTests || loadState() || EMPTY_STATE;
debug("initial redux state: %o", persistedState);
const composeEnhancers =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const thunkMiddlewareEnhancer = applyMiddleware(
  thunk as ThunkMiddleware<AppState, ActionTypes.AppAction>
);
const enhancers: StoreEnhancer<
  {
    dispatch: ThunkDispatch<AppState, undefined, ActionTypes.AppAction>;
  },
  {}
> = composeEnhancers(thunkMiddlewareEnhancer);
const store = createStore(rootReducer, persistedState, enhancers);

// Auto-save store to local storage
store.subscribe(
  lazyThrottle(() => {
    saveStore();
  }, 1000)
);

/**
 * Saves store to local storage, selecting which parts of store need to be saved.
 */
export function saveStore(): void {
  const fullState = store.getState();
  const stateToStore: AppState = {
    pendingAuthentication: fullState.pendingAuthentication,
  };
  saveState(stateToStore);
}

export function getAuthentication(): Authentication | undefined {
  return store.getState()?.authentication || undefined;
}

export function getPendingAuthentication(): PendingAuthentication | undefined {
  return store.getState()?.pendingAuthentication || undefined;
}

export function getLogoutCompleted(): LogoutCompleted | undefined {
  return store.getState()?.logoutCompleted || undefined;
}

export function hasErrors(): boolean {
  return (
    store.getState() &&
    store.getState().errors !== undefined &&
    store.getState().errors !== null &&
    (store.getState().errors as AppError<any>[]).length > 0
  );
}

export default store;
